import {Asset, AssetType, TagData, WebSourceAsset, XMLData} from "../../redux/assets-slice";
import { Bookmark } from "../../redux/bookmarks-slice";
import {t} from "../../localization";
import path from "path";
import LocalScene from "../../viewer/js/projections";
import { fse, getTemporaryFile } from "../../electron-modules";
import { PythonExecutable } from "../../executable";
import { getLocalizedProjectName, getMatrixValues, pathWithExtension } from "../../utilities";
import { engineCloudURL } from "../../urls";
import { CustomURL } from "../../redux/custom-links-slice";
import {
  getBaseAsset,
  getTagsData,
  getImageData,
  getModelLayers,
  getWebmapLayers,
  withOriginalID
} from "../utils/misc";
import { StatusCode } from "../../types/cloud-export";
import manifest from "../../../package.json";

const SHP_FILE_TYPES = ["shp", "dbf"];
const POTREE_FILES = ['hierarchy.bin', 'octree.bin', 'metadata.json'];

interface ExporterOpts {
  name: string;
  assets: Asset[];
  state: any;
  update: boolean;
  appCode: string;
  onError?(title: string, message: string): void;
  onStart?(statuses: {}): void;
  onUpdate?(statuses: {}): void;
  onProgress?(percent: number): void;
  onFinish?(projectID: string): void;
}

const exporterOptsDefaults = {
  onError: () => {},
  onStart: () => {},
  onUpdate: () => {},
  onProgress: () => {},
  onFinish: () => {}
};

const createProject = async (state, update: boolean): Promise<any> => {
  const folders = state.folders.map(folder => {
    const assets = state.assets
      .filter(asset => asset.folderID === folder.id) as Asset[];

    const tags = assets
      .filter(asset => asset.type === AssetType.Tag)
      .map(tagAsset => ({
        ...getBaseAsset(tagAsset),
        texture: (tagAsset.data as TagData).texture,
        size: (tagAsset.data as TagData).size,
        tags: getTagsData(tagAsset)
      }));

    const panoramics = assets
      .filter(asset => asset.type === AssetType.Panoramic)
      .map(panoAsset => ({
        ...getBaseAsset(panoAsset),
        images: getImageData(panoAsset)
      }));

    const planars = assets
      .filter(asset => asset.type === AssetType.Planar)
      .map(panoAsset => ({
        ...getBaseAsset(panoAsset),
        images: getImageData(panoAsset)
      }));

    const pointClouds = assets
      .filter(asset => {
        return (asset.type === AssetType.LAS)
          || (asset.type === AssetType.E57Points)
          || (asset.type === AssetType.Potree);
      })
      .map(pointCloudAsset => ({
        ...getBaseAsset(pointCloudAsset),
        type: AssetType.Potree
      }));

    const orthomosaics = assets
      .filter(asset => asset.type === AssetType.OrthoMosaic)
      .map(orthoAsset => ({
        ...getBaseAsset(orthoAsset),
        imageData: orthoAsset.data
      }));

    const landxmls = assets
      .filter(asset => asset.type === AssetType.LandXML)
      .map(landXMLAsset => ({
        ...getBaseAsset(landXMLAsset),
        ...(landXMLAsset.data as XMLData)
      }));

    const ifcFiles = assets
      .filter(asset => asset.type === AssetType.IFC)
      .map(ifcAsset => ({
        ...getBaseAsset(ifcAsset),
        fileName: path.basename(ifcAsset.path)
      }));

    const shpFiles = assets
      .filter(asset => asset.type === AssetType.SHP)
      .map(shpAsset => ({
        ...getBaseAsset(shpAsset),
        fileName: path.basename(shpAsset.path),
        layers: getModelLayers(shpAsset)
      }));

    const dxfFiles = assets
      .filter(asset => asset.type === AssetType.DXF)
      .map(dxfAsset => ({
        ...getBaseAsset(dxfAsset),
        fileName: path.basename(dxfAsset.path),
        layers: getModelLayers(dxfAsset)
      }));

    const webmapSources = assets
      .filter(asset => asset.type === AssetType.Webmap)
      .map((webmapAsset: WebSourceAsset) => ({
        ...getBaseAsset(webmapAsset),
        url: webmapAsset.data.url,
        map_type: webmapAsset.data.type,
        public: webmapAsset.data.public,
        layers: getWebmapLayers(webmapAsset)
      }));

    return {
      name: folder.name,
      originalId: folder.id,
      default: folder.default,
      assets: {
        tags,
        panoramics,
        planars,
        pointClouds,
        orthomosaics,
        landxmls,
        ifcFiles,
        shpFiles,
        dxfFiles,
        webmapSources
      }
    };
  });

  const projections = {
    data: {
      type: state.projections.data.type,
      name: state.projections.data.name,
      default: state.projections.data.default,
      string: state.projections.data.string,
      transform: state.projections.data.transform
    },
    view: {
      type: state.projections.view.type,
      name: state.projections.view.name,
      default: state.projections.view.default,
      string: state.projections.view.string,
      transform: state.projections.view.transform
    }
  };

  const bookmarks = state.bookmarks.map((bookmark: Bookmark) => {
    return withOriginalID(bookmark);
  });

  const customLinks = state.custom_links.map((link: CustomURL) => {
    return withOriginalID(link);
  });

  const {success, rotation, offset}
    = await getMatrixValues(state.camera.transform);

  const transform = success ? {
    rotation: rotation.toArray(),
    translation: offset.toArray()
  } : null;

  const adjustments = Object.keys(state.camera.adjustments).map(camera => {
    const rotation = state.camera.adjustments[camera];
    return {camera, rotation};
  });

  const cameraState = {
    transform: transform,
    adjustments: adjustments,
    height: state.camera.height,
    aerialState: state.camera.aerialState,
    sceneState: state.camera.sceneState
  };

  const method = update ? "PUT" : "POST";
  const localizedProjectName = getLocalizedProjectName(state.project.name);

  const projectState = JSON.stringify({
    name: localizedProjectName,
    originalId: state.project.id,
    settings: state.settings,
    appVersion: manifest.version,
    configVersion: state.project.configVersion,
    measurementUnits: state.projections.measureUnits,
    folders,
    bookmarks,
    customLinks,
    projections,
    cameraState
  });

  const body = new Blob([projectState], {
    type: 'text/plain'
  });

  const request = await fetch(`${engineCloudURL}/api/projects`, {
    method,
    body,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  const response = await request.json();

  return response;
};

interface UploadItem {
  id: string;
  url: string;
  paths: string[];
  name: string;
  processPoints?: boolean;
  units?: string;
}

const getAssetsToUpload = (project, opts) => {
  const projectID = project.id;
  const uploadList: UploadItem[] = [];

  project.uploads.forEach(({uploadId, originalId}) => {
    const asset = opts.state.assets.find(asset => asset.id === originalId);

    if ((asset.type === AssetType.Panoramic) || (asset.type === AssetType.Planar)) {
      asset.data.forEach(image => {
        uploadList.push({
          id: image.id,
          url: getUploadURL(projectID, uploadId),
          paths: [image.path],
          name: image.name
        });
      });
    }

    if ((asset.type === AssetType.LAS) ||  (asset.type === AssetType.E57Points)) {
      uploadList.push({
        id: asset.id,
        url: getUploadURL(projectID, uploadId),
        paths: [asset.path],
        name: asset.name,
        processPoints: true,
        units: LocalScene.dataProjectionUnits
      });
    }

    if (asset.type === AssetType.Potree) {
      uploadList.push({
        id: asset.id,
        url: getUploadURL(projectID, uploadId),
        paths: POTREE_FILES.map(filePath => `${asset.path}/${filePath}`),
        name: asset.name,
      });
    }

    if ((asset.type === AssetType.IFC) || (asset.type === AssetType.DXF)) {
      uploadList.push({
        id: asset.id,
        url: getUploadURL(projectID, uploadId),
        paths: [asset.path],
        name: asset.name
      });
    }

    if (asset.type === AssetType.SHP) {
      uploadList.push({
        id: asset.id,
        url: getUploadURL(projectID, uploadId),
        paths: SHP_FILE_TYPES.map(fileType => pathWithExtension(asset.path, fileType)),
        name: asset.name,
      });
    }
  });

  return uploadList;
};

const getUploadURL = (projectID, assetID) => {
  return `${engineCloudURL}/api/projects/${projectID}/assets/${assetID}/files`;
};

export const getCloudExporter = () => {
  let projectID;
  let statuses = {};

  const exe = new PythonExecutable();

  const startExport = async (opts: ExporterOpts) => {
    opts = {...exporterOptsDefaults, ...opts};

    const response = await createProject(opts.state, opts.update);

    if (response.detail) {
      // Error creating project (incorrect values in project state)
      opts.onError(t("dialog.general-error-title"), t("dialog.export-error-cloud"));
      return;
    }

    if (response.version_mismatch) {
      // Config version is lower than required server version
      opts.onError(t("dialog.general-error-title"), t('export.version_mismatch', {
        current: manifest.version,
        server: response.minimum_version
      }));
      return;
    }

    projectID = response.id;

    const assets = getAssetsToUpload(response, opts);
    if (assets.length === 0) {
      opts.onFinish(projectID);
      return;
    }

    // Save assets to temporary json file
    const tempOutputPath = getTemporaryFile("json");
    const text = JSON.stringify(assets, null, 2);
    await fse.writeFile(tempOutputPath, text);

    let commands = [
      "-p", "upload_engine_assets",
      "--engine_cloud_url", engineCloudURL,
      "--input_path", tempOutputPath,
      "--app_code", opts.appCode
    ];

    exe.run({
      commands,
      saveLogs: true,
      onLine: jsonData => {
        if (jsonData.progress) {
          opts.onProgress(jsonData.progress);
        }

        if (jsonData.start) {
          statuses = jsonData.start;
          opts.onStart(statuses);
        }

        if (jsonData.update) {
          const status = jsonData.update;

          try {
            statuses[status.id].status_code = status.status_code;
            opts.onUpdate(statuses);
          } catch {
            console.warn("Error updating status row");
          }
        }
      },
      onClose: (error) => {
        if (error) return;
        opts.onFinish(projectID);
      }
    });
  };

  const cancelExport = (update: boolean) => {
    exe.destroy();

    if (update || !projectID) {
      return;
    }

    fetch(`${engineCloudURL}/api/projects/${projectID}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(() => {
      projectID = null;
    });
  };

  return {
    startExport,
    cancelExport
  };
};